import { EnvSettings } from '@e2m-customer-portal/shared';
import { ProviderProps, ReactElement, useContext } from 'react';
import { suspend } from 'suspend-react';
import { fetchWithRequestId, withRequestId } from '../../utils';
import { EnvSettingsContext } from './env-settings-context';

export type EnvSettingsProviderProps = Omit<ProviderProps<EnvSettings>, 'value'>;

export function EnvSettingsProvider(props: EnvSettingsProviderProps): ReactElement<EnvSettingsProviderProps> {
  const envSettings = suspend(async () => {
    const response = await fetchWithRequestId()('/api/v1/env-settings');
    if (!response.ok) {
      throw withRequestId(new Error('Failed to retrieve environment settings.'), response.requestId);
    }
    return response.json() as Promise<EnvSettings>;
  }, [EnvSettingsProvider.name]);

  return <EnvSettingsContext.Provider value={envSettings} {...props} />;
}

export function useEnvSettings(): EnvSettings {
  const envSettings = useContext(EnvSettingsContext);
  if (!envSettings) {
    throw new Error('Expected settings to be loaded.');
  }

  return envSettings;
}

export function useCurrentEnv(): 'LOCAL' | 'DEV' | 'STAGE' | 'PROD' {
  const { environment } = useEnvSettings();

  if (document.location.hostname.includes('localhost')) return 'LOCAL';
  if (/develop/i.test(environment)) return 'DEV';
  if (/stage/i.test(environment)) return 'STAGE';
  return 'PROD';
}
